import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { AuctionActivityRole, AuctionActivityState, AuctionResultState } from '@core/models/auction-result.state';
import { AuctionOutcome, AuctionStatus, BidStatus } from '@core/models/auction.enum';
import { AuctionState, BidOutcomeState, BidState } from '@core/models/auction.state';
import { FulfilmentMethod, ListingStatus } from '@core/models/listing.enum';
import { ListingDetailState } from '@core/models/listing.state';
import { OrderGroupStatus } from '@core/models/order.enum';
import { OrderGroupState, OrderState } from '@core/models/order.state';
import { resolveBidIncrement } from '@core/models/platform-config.state';
import { ShopperState } from '@core/models/shopper.state';
import { StoreState } from '@core/models/store.state';
import { MockDataService } from '@core/services/mock-data.service';

import { seedListings } from '../listing/listing.seed';
import { mockResponse } from '../mock-response';
import { SHIPPING_ETA_FALLBACK, buildOrderTotals, mintPickupTicket, nextOrderRef } from '../order/order-build';
import { seedOrders } from '../order/order.seed';
import { seedPlatformConfig } from '../platform-config/platform-config.seed';
import { seedShoppers } from '../shopper/shopper.seed';
import { seedStores } from '../store/store.seed';
import { AuctionRepository } from './auction.repository';
import { AuctionProxyEntry, seedAuctionProxies, seedAuctionResults, seedAuctions, seedBids } from './auction.seed';

/**
 * Auction policy comes from the platform config (ADM-02), not from constants
 * scattered through the engine. In production these are server-side values the
 * Admin Portal owns; here the mock engine plays that role.
 */
const platformConfig = seedPlatformConfig();

/** Anti-snipe window: a bid this close to the end extends the auction (AUC-05). */
const SNIPE_WINDOW_MS = platformConfig.antiSnipeWindowMs;

/** AUC-02 increment for a given standing price, from the platform band table. */
const incrementFor = (currentPrice: number): number => resolveBidIncrement(platformConfig.bidIncrements, currentPrice);

/**
 * In-memory auction engine (HLD §9): proxy bidding, anti-snipe extension and
 * a close sweep that settles each auction exactly once - writing the
 * `AuctionResult` row and auto-charging the winner's card on file into a Paid
 * order (HLD §9.3–§9.4, D-05). Denormalized auction fields on the listing row
 * are patched in the same operation, mirroring the single-writer catalog read
 * model.
 */
@Injectable()
export class AuctionMockRepository implements AuctionRepository {
  private readonly mockData = inject(MockDataService);
  private readonly auctions = this.mockData.collection<AuctionState>('auctions', seedAuctions);
  private readonly bids = this.mockData.collection<BidState>('bids', seedBids);
  private readonly proxies = this.mockData.collection<AuctionProxyEntry>('auction-proxies', seedAuctionProxies);
  private readonly results = this.mockData.collection<AuctionResultState>('auction-results', seedAuctionResults);
  private readonly listings = this.mockData.collection<ListingDetailState>('listings', seedListings);
  private readonly orders = this.mockData.collection<OrderState>('orders', seedOrders);
  private readonly stores = this.mockData.collection<StoreState>('stores', seedStores);
  private readonly shoppers = this.mockData.collection<ShopperState>('shoppers', seedShoppers);

  getByListingId(listingId: number): Observable<AuctionState | null> {
    this.closeEnded();
    return mockResponse(this.auctions.list().find(a => a.listingId === listingId) ?? null);
  }

  listBids(auctionId: number): Observable<BidState[]> {
    const history = this.bids
      .list()
      .filter(b => b.auctionId === auctionId)
      .sort((a, b) => b.placedAt.getTime() - a.placedAt.getTime());
    return mockResponse(history);
  }

  placeBid(
    auctionId: number,
    shopperId: number,
    maxBid: number,
    fulfilment?: FulfilmentMethod
  ): Observable<BidOutcomeState> {
    this.closeEnded();
    const auction = this.auctions.get(auctionId);
    if (!auction || auction.status !== AuctionStatus.Live) {
      return mockResponse(this.rejected(auction, 'This auction has ended.'));
    }
    if (maxBid < auction.minNextBid) {
      return mockResponse(this.rejected(auction, `Enter ${this.money(auction.minNextBid)} or more.`));
    }

    const now = new Date();
    const standing = this.standingProxy(auction);

    let displayedBid: number;
    let isHighBidder: boolean;

    if (maxBid > standing.maxBid) {
      // Challenger wins: displayed bid is the minimum needed to beat the old max.
      displayedBid = Math.min(maxBid, standing.maxBid + auction.bidIncrement);
      isHighBidder = true;
    } else {
      // Standing proxy defends: it auto-bids just above the challenger.
      displayedBid = Math.min(standing.maxBid, maxBid + auction.bidIncrement);
      isHighBidder = false;
    }
    // Losing proxies are recorded too - they are the shopper's participation
    // trail for My Bids (ACC-03) and the PDP lost state (AUC-07).
    this.recordProxy(auctionId, shopperId, maxBid);

    for (const prior of this.bids.list().filter(b => b.auctionId === auctionId && b.isHighBid)) {
      this.bids.patch(prior.id, { isHighBid: false, status: BidStatus.Outbid });
    }

    const shopperHandle = this.handleFor(shopperId);
    const isReturningBidder = this.bids.list().some(b => b.auctionId === auctionId && b.bidderHandle === shopperHandle);
    if (isHighBidder) {
      this.appendBid(auctionId, shopperHandle, displayedBid, now, true);
    } else {
      // Challenger's losing bid, then the defender's automatic counter on top.
      this.appendBid(auctionId, shopperHandle, Math.min(maxBid, displayedBid), now, false);
      this.appendBid(auctionId, this.handleFor(standing.shopperId), displayedBid, now, true);
    }

    // Anti-snipe: a late bid pushes the end out to a full window (AUC-05).
    let endsAt = auction.endsAt;
    let extensionCount = auction.extensionCount;
    if (endsAt.getTime() - now.getTime() < SNIPE_WINDOW_MS) {
      endsAt = new Date(now.getTime() + SNIPE_WINDOW_MS);
      extensionCount += 1;
    }

    const bidCount = auction.bidCount + (isHighBidder ? 1 : 2);
    const reserveMet = auction.reservePrice === null || displayedBid >= auction.reservePrice;
    // The bid crossing a band boundary changes the increment (AUC-02), so it
    // is resolved against the new standing price rather than carried forward.
    const nextIncrement = incrementFor(displayedBid);
    const updated = this.auctions.patch(auctionId, {
      currentBid: displayedBid,
      bidIncrement: nextIncrement,
      minNextBid: displayedBid + nextIncrement,
      bidCount,
      bidderCount: auction.bidderCount + (isReturningBidder ? 0 : 1),
      highBidderId: isHighBidder ? shopperId : standing.shopperId,
      // The high bid carries its owner's fulfilment choice; a defended proxy
      // keeps whatever the standing bidder chose (HLD D-05).
      highBidderFulfilment: isHighBidder ? (fulfilment ?? null) : auction.highBidderFulfilment,
      endsAt,
      extensionCount,
      reserveMet,
      // First bid removes Buy-It-Now (AUC-04).
      binAvailable: false
    })!;

    this.listings.patch(auction.listingId, {
      currentBid: updated.currentBid,
      bidCount: updated.bidCount,
      endsAt: updated.endsAt,
      reserveMet: updated.reserveMet
    });

    return mockResponse({
      accepted: true,
      reason: null,
      displayedBid: updated.currentBid,
      isHighBidder,
      minNextBid: updated.minNextBid,
      endsAt: updated.endsAt
    });
  }

  getResult(auctionId: number): Observable<AuctionResultState | null> {
    this.closeEnded();
    const result = this.results.get(auctionId);
    return mockResponse(result ? { ...result } : null);
  }

  listActivity(shopperId: number): Observable<AuctionActivityState[]> {
    this.closeEnded();
    const items: AuctionActivityState[] = [];
    for (const proxy of this.proxies.list().filter(p => p.shopperId === shopperId)) {
      const auction = this.auctions.get(proxy.auctionId);
      const listing = auction ? this.listings.get(auction.listingId) : undefined;
      if (!auction || !listing) continue;
      const result = this.results.get(auction.id) ?? null;
      items.push({
        auctionId: auction.id,
        listingId: listing.id,
        listingTitle: listing.title,
        listingPhotoUrl: listing.primaryPhotoUrl,
        listingSlugId: `${listing.slug}-${listing.id}`,
        role: this.roleFor(auction, result, shopperId),
        currentOrFinalBid: result?.winningAmount ?? auction.currentBid,
        myMaxBid: proxy.maxBid,
        endsAt: auction.endsAt,
        orderRef: result?.winnerId === shopperId ? result.orderRef : null
      });
    }
    // Live rows first, soonest ending; settled rows after, newest ended first.
    items.sort((a, b) => {
      const aLive = this.isLiveRole(a.role);
      const bLive = this.isLiveRole(b.role);
      if (aLive !== bLive) return aLive ? -1 : 1;
      return aLive ? a.endsAt.getTime() - b.endsAt.getTime() : b.endsAt.getTime() - a.endsAt.getTime();
    });
    return mockResponse(items);
  }

  sweepClosed(): Observable<number> {
    return mockResponse(this.closeEnded());
  }

  /**
   * Close engine (mock analogue of HLD §9.3): auctions past `endsAt` settle -
   * reserve met → Sold, otherwise Ended - and each close writes its
   * `AuctionResult` row exactly once. Idempotent per auction.
   */
  private closeEnded(): number {
    const now = new Date();
    let closed = 0;
    for (const auction of this.auctions.list()) {
      if (auction.status !== AuctionStatus.Live || auction.endsAt.getTime() > now.getTime()) continue;
      const sold = auction.bidCount > 0 && auction.reserveMet;
      this.auctions.patch(auction.id, { status: AuctionStatus.Closed });
      this.listings.patch(auction.listingId, { status: sold ? ListingStatus.Sold : ListingStatus.Ended });
      this.settle(auction, sold, now);
      closed += 1;
    }
    return closed;
  }

  /**
   * Phase-2 settle (HLD §9.3): write the result row - skipping auctions that
   * already have one, the mock analogue of the unique-index conflict - then
   * auto-charge a known winning shopper's card on file into a Paid order
   * (HLD §9.4, D-05) and stamp its ref back onto the result.
   */
  private settle(auction: AuctionState, sold: boolean, settledAt: Date): void {
    if (this.results.get(auction.id)) return;
    const outcome = sold
      ? AuctionOutcome.Won
      : auction.bidCount === 0
        ? AuctionOutcome.NoBids
        : AuctionOutcome.ReserveNotMet;
    const winnerId = sold ? auction.highBidderId : null;
    this.results.upsert({
      id: auction.id,
      auctionId: auction.id,
      listingId: auction.listingId,
      winnerId,
      winningAmount: sold ? auction.currentBid : null,
      outcome,
      settledAt,
      orderRef: null
    });
    if (winnerId !== null) {
      const orderRef = this.chargeWinner(auction, winnerId, settledAt);
      if (orderRef) this.results.patch(auction.id, { orderRef });
    }
  }

  /** Card-on-file auto-charge (D-05): mint the winner's Paid order. */
  private chargeWinner(auction: AuctionState, winnerId: number, settledAt: Date): string | null {
    const listing = this.listings.get(auction.listingId);
    const shopper = this.shoppers.get(winnerId);
    if (!listing || !shopper) return null;

    const store = this.stores.get(listing.storeId);
    // Honor the fulfilment the winner chose with their bid; bids that carried
    // no choice keep the legacy pickup-first default (HLD D-05).
    const isPickup =
      auction.highBidderFulfilment !== null
        ? auction.highBidderFulfilment === FulfilmentMethod.Pickup
        : listing.pickupAvailable;
    const group: OrderGroupState = {
      id: 1,
      tenantId: listing.tenantId,
      tenantName: listing.tenantName,
      storeId: listing.storeId,
      storeName: listing.storeName,
      storeAddressLine: store?.addressLine ?? '',
      fulfilment: isPickup ? FulfilmentMethod.Pickup : FulfilmentMethod.Shipping,
      status: OrderGroupStatus.Paid,
      lines: [
        {
          id: 1,
          listingId: listing.id,
          title: listing.title,
          condition: listing.condition,
          photoUrl: listing.primaryPhotoUrl,
          unitPrice: auction.currentBid
        }
      ],
      itemsSubtotal: auction.currentBid,
      shippingAmount: isPickup ? 0 : (listing.shippingPrice ?? 0),
      pickupTicket: isPickup ? mintPickupTicket(store) : null,
      trackingNumber: null,
      etaLabel: isPickup ? null : (listing.fulfilment.shippingEtaLabel ?? SHIPPING_ETA_FALLBACK),
      refund: null
    };

    const order = this.orders.upsert({
      id: this.orders.allocateId(),
      orderRef: nextOrderRef(this.orders.list()),
      placedAt: settledAt,
      email: shopper.email,
      shopperId: winnerId,
      totals: buildOrderTotals([group]),
      groups: [group]
    });
    return order.orderRef;
  }

  private roleFor(auction: AuctionState, result: AuctionResultState | null, shopperId: number): AuctionActivityRole {
    if (auction.status === AuctionStatus.Live) {
      return auction.highBidderId === shopperId ? 'winning' : 'outbid';
    }
    if (result?.outcome === AuctionOutcome.Won) {
      return result.winnerId === shopperId ? 'won' : 'lost';
    }
    return 'ended';
  }

  private isLiveRole(role: AuctionActivityRole): boolean {
    return role === 'winning' || role === 'outbid';
  }

  /**
   * The standing proxy - the highest recorded proxy row, as long as it still
   * covers the displayed bid; otherwise the (possibly anonymous) seeded high
   * bidder implied by the auction row itself.
   */
  private standingProxy(auction: AuctionState): { shopperId: number | null; maxBid: number } {
    let top: AuctionProxyEntry | null = null;
    for (const row of this.proxies.list()) {
      if (row.auctionId !== auction.id) continue;
      if (!top || row.maxBid > top.maxBid) top = row;
    }
    if (top && top.maxBid >= auction.currentBid) return { shopperId: top.shopperId, maxBid: top.maxBid };
    return { shopperId: auction.highBidderId, maxBid: auction.currentBid };
  }

  /** Record (or raise) the shopper's proxy max for an auction. */
  private recordProxy(auctionId: number, shopperId: number, maxBid: number): void {
    const existing = this.proxies.list().find(p => p.auctionId === auctionId && p.shopperId === shopperId);
    if (existing) {
      this.proxies.patch(existing.id, { maxBid: Math.max(existing.maxBid, maxBid) });
    } else {
      this.proxies.upsert({ id: this.proxies.allocateId(), auctionId, shopperId, maxBid });
    }
  }

  private appendBid(auctionId: number, bidderHandle: string, amount: number, placedAt: Date, isHighBid: boolean): void {
    this.bids.upsert({
      id: this.bids.allocateId(),
      auctionId,
      bidderHandle,
      amount,
      placedAt,
      status: isHighBid ? BidStatus.Active : BidStatus.Outbid,
      isHighBid
    });
  }

  /** Stable anonymized handle (AUC-08): seeded proxies have no shopper id. */
  private handleFor(shopperId: number | null): string {
    if (shopperId === null) return 'd***2';
    return `j***${shopperId % 10}`;
  }

  private rejected(auction: AuctionState | undefined, reason: string): BidOutcomeState {
    return {
      accepted: false,
      reason,
      displayedBid: auction?.currentBid ?? 0,
      isHighBidder: false,
      minNextBid: auction?.minNextBid ?? 0,
      endsAt: auction?.endsAt ?? new Date()
    };
  }

  private money(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }
}
